import { produce, immerable } from "immer";
import { type VendorDataItem, type InputVendorDataItem } from "../../gql-generated";
import { VendorDataEntryModel } from "./VendorDataEntryModel";

export class VendorDataCollection {
    public [immerable] = true;
    public vendorDataIndex = 0;
    public maxVendorDataCount = 10;
    public vendorData: readonly VendorDataEntryModel[];
    public hasChanged: boolean = false;

    public constructor(entries: readonly VendorDataItem[]) {
        this.vendorData = entries
            ? entries.map((data) => new VendorDataEntryModel(this.vendorDataIndex++, data, true, true))
            : [];
    }
    public addEmptyVendorData() {
        if (this.isMaxCount) {
            return this;
        }
        return produce(this, (draft) => {
            draft.vendorData.push(
                new VendorDataEntryModel(draft.vendorDataIndex++, { key: null, value: null }, false, false),
            );
            draft.hasChanged = true;
        });
    }

    public updateVendorData(entry: VendorDataEntryModel): VendorDataCollection {
        return produce(this, (draft) => {
            const collectionIndex = draft.vendorData.findIndex(({ index }) => entry.index === index);
            draft.vendorData[collectionIndex] = entry;
            draft.hasChanged = true;
        });
    }

    public removeVendorData(index: number) {
        return produce(this, (draft) => {
            draft.vendorData = draft.vendorData.filter((data) => data.index !== index);
            draft.hasChanged = true;
        });
    }

    public replaceVendorData(entries: readonly VendorDataItem[]): VendorDataCollection {
        return produce(this, (draft) => {
            draft.vendorData = entries.map(
                (data) => new VendorDataEntryModel(draft.vendorDataIndex++, data, true, true),
            );
            draft.hasChanged = false;
        });
    }

    public get isMaxCount() {
        return this.vendorData.length >= this.maxVendorDataCount;
    }

    public get hasDuplicates() {
        return this.getNonUniqueKeys().size > 0;
    }

    public get isCollectionValid() {
        return !this.hasDuplicates && this.vendorData.every((entry) => entry.isValid);
    }

    public getNonUniqueKeys(): Set<string> {
        const keys = new Set<string>();
        const duplicatedKeys = new Set<string>();

        for (const entry of this.vendorData) {
            if (keys.has(entry.data.key as string)) {
                duplicatedKeys.add(entry.data.key as string);
            } else {
                keys.add(entry.data.key as string);
            }
        }

        return duplicatedKeys;
    }

    public toDataObjects(): InputVendorDataItem[] {
        return this.vendorData.map<InputVendorDataItem>(({ data: { key, value } }) => ({
            key,
            value,
        }));
    }
}
